import React, { useCallback, useMemo } from "react"

import { useCreatorResolver } from "@/organization"
import { TagDotsPreview } from "@/tags"
import { CopyableId, type ColumnDef, type SortDescriptor } from "@coval/ui"

import { EntityPickerDialog } from "@/components/entity-picker/EntityPickerDialog"
import type { AgentRecord } from "@/types/api/agent"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

import { formatAgentType } from "../utils/agentTypeLabel"

// Trimmed from the agents list page's set to what a picker needs — no creator
// column, and the name carries any reason the row can't be picked.
const NAME_COLUMN_WIDTH = 240
const NAME_COLUMN_MIN_WIDTH = 180
const TYPE_COLUMN_WIDTH = 140
const PHONE_COLUMN_WIDTH = 140
const DATE_COLUMN_WIDTH = 100
const TAGS_COLUMN_WIDTH = 110
const ID_COLUMN_WIDTH = 104

const DEFAULT_SORT: SortDescriptor = {
  columnId: "last_updated_at",
  direction: "desc",
}

// Column model kept at module scope (deps passed in) so the JSX cells don't
// trip react/no-unstable-nested-components; the component memoizes the result.
function buildAgentPickerColumns({
  resolveCreator,
  tagColorMap,
}: {
  resolveCreator: (creator: string | null | undefined) => string
  tagColorMap?: Record<string, string | null | undefined>
}): ColumnDef<AgentRecord>[] {
  return [
    {
      id: "display_name",
      header: "Name",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (agent) => (
        <span className="min-w-0 truncate font-semibold">
          {agent.display_name || agent.id}
        </span>
      ),
    },
    {
      id: "model_type",
      header: "Type",
      width: TYPE_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (agent) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {formatAgentType(agent.model_type || "")}
        </span>
      ),
    },
    {
      id: "phone_number",
      header: "Phone",
      width: PHONE_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (agent) => (
        <span className="min-w-0 truncate font-mono text-coval-text-muted">
          {agent.phone_number || "-"}
        </span>
      ),
    },
    {
      id: "last_updated_at",
      header: "Updated",
      width: DATE_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (agent) => (
        <span
          className="whitespace-nowrap text-coval-text-muted"
          title={formatFullTimestamp(agent.last_updated_at)}
        >
          {formatRelativeTimestamp(agent.last_updated_at)}
        </span>
      ),
    },
    {
      id: "created_by",
      header: "Created by",
      width: DATE_COLUMN_WIDTH,
      enableResizing: false,
      cell: (agent) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {resolveCreator(agent.created_by_user_id)}
        </span>
      ),
    },
    {
      id: "tags",
      header: "Tags",
      width: TAGS_COLUMN_WIDTH,
      align: "right",
      flex: false,
      enableResizing: false,
      cell: (agent) => (
        <div className="flex min-w-0 justify-end">
          <TagDotsPreview tags={agent.tags ?? []} tagColorMap={tagColorMap} />
        </div>
      ),
    },
    {
      id: "id",
      header: "ID",
      variant: "id",
      width: ID_COLUMN_WIDTH,
      pinned: "right",
      enableResizing: false,
      cell: (agent) => <CopyableId id={agent.id} label="Copy Agent ID" />,
    },
  ]
}

interface AgentPickerDialogProps {
  isOpen: boolean
  onCancel: () => void
  onApply: () => void
  agents: AgentRecord[]
  isLoading?: boolean
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  tagColorMap?: Record<string, string | null | undefined>
  createAction?: React.ReactNode
}

export const AgentPickerDialog: React.FC<AgentPickerDialogProps> = ({
  isOpen,
  onCancel,
  onApply,
  agents,
  isLoading = false,
  selectedIds,
  onSelectionChange,
  tagColorMap,
  createAction,
}) => {
  const { resolveCreator } = useCreatorResolver()

  const columns = useMemo(
    () =>
      buildAgentPickerColumns({
        resolveCreator,
        tagColorMap,
      }),
    [resolveCreator, tagColorMap],
  )

  const matchesSearch = useCallback(
    (agent: AgentRecord, query: string) =>
      [
        agent.display_name,
        agent.id,
        agent.phone_number,
        formatAgentType(agent.model_type || ""),
        ...(agent.tags ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    [],
  )

  return (
    <EntityPickerDialog<AgentRecord>
      isOpen={isOpen}
      onCancel={onCancel}
      onApply={onApply}
      title="Select Agents"
      items={agents}
      columns={columns}
      getItemId={(agent) => agent.id}
      getItemLabel={(agent) => agent.display_name || agent.id}
      matchesSearch={matchesSearch}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      isLoading={isLoading}
      defaultSort={DEFAULT_SORT}
      rowAttributes={(agent) => ({
        "data-testid": `agent-option-${agent.id}`,
        "data-agent-name": agent.display_name || undefined,
      })}
      searchPlaceholder="Search agents by name, type, phone, or tag..."
      createAction={createAction}
      emptyState="No agents yet"
      searchEmptyState="No agents match your search"
      data-testid="agent-picker-dialog"
    />
  )
}
